import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { SubPage } from "@/components/sub-page";

type EditorialListItem = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  author: string | null;
  cover_image_url: string | null;
  published_at: string | null;
  body_html?: string | null;
};

function coverFrom(item: EditorialListItem): string | null {
  if (item.cover_image_url) return item.cover_image_url;
  const html = item.body_html ?? "";
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  if (m) return m[1];
  const bg = html.match(/background-image\s*:\s*url\((['"]?)([^)'"]+)\1\)/i);
  return bg ? bg[2] : null;
}

const listEditoriales = createServerFn({ method: "GET" }).handler(async () => {
  const supa = createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
  const { data, error } = await supa
    .from("editoriales")
    .select("id,slug,title,subtitle,author,cover_image_url,published_at,body_html")
    .eq("is_published", true)
    .order("sort_order", { ascending: true })
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(50);
  if (error) return [] as EditorialListItem[];
  return (data ?? []) as EditorialListItem[];
});

export const Route = createFileRoute("/comunicaciones/editoriales/")({
  loader: () => listEditoriales(),
  head: () => ({
    meta: [
      { title: "Editoriales | Mine Class" },
      { name: "description", content: "Columnas y editoriales de Mine Class sobre formación, tecnología e innovación en la industria minera." },
      { property: "og:title", content: "Editoriales Mine Class" },
      { property: "og:description", content: "Columnas y editoriales de Mine Class sobre formación, tecnología e innovación en la industria minera." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/comunicaciones/editoriales" }],
  }),
  errorComponent: () => <SubPage section="comunicaciones" eyebrow="Comunicaciones" title="Editoriales" />,
  notFoundComponent: () => <SubPage section="comunicaciones" eyebrow="Comunicaciones" title="Editoriales" />,
  component: Page,
});

function Page() {
  const items = Route.useLoaderData() as EditorialListItem[];
  return (
    <SubPage
      section="comunicaciones"
      eyebrow="Comunicaciones"
      title="Editoriales"
      intro="Columnas y reflexiones del equipo Mine Class sobre la transformación de la minería."
    >
      {items.length === 0 ? (
        <p className="text-[var(--color-on-surface-variant)]">Aún no hay editoriales publicadas.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6 not-prose">
          {items.map((e) => {
            const cover = coverFrom(e);
            return (
            <Link
              key={e.id}
              to="/comunicaciones/editoriales/$slug"
              params={{ slug: e.slug }}
              className="bg-white border border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)] transition-colors p-6 block"
            >
              {cover && (
                <img src={cover} alt={e.title} loading="lazy" className="w-full h-48 object-cover mb-4" />
              )}
              <h2 className="text-xl font-bold text-[var(--color-brand-purple)]">{e.title}</h2>
              {e.subtitle && (
                <p className="text-sm text-[var(--color-on-surface-variant)] mt-2 line-clamp-3">{e.subtitle}</p>
              )}
              <p className="text-xs text-[var(--color-on-surface-variant)] mt-3">
                {e.author}
                {e.author && e.published_at ? " · " : ""}
                {e.published_at ? new Date(e.published_at).toLocaleDateString("es-CL", { dateStyle: "long" }) : ""}
              </p>
            </Link>
            );
          })}
        </div>
      )}
    </SubPage>
  );
}
